import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, useTheme } from '@mui/material';
import {
    complementaryInfoMailToClientIcon,
    depositIcon,
    FaOptionIcon,
    saveIcon,
    undoIcon,
} from '@europrocurement/l2d-icons';
import { useDossierFormulaireContext } from '@europrocurement/l2d-modules';
import { FlexyButton } from '@europrocurement/flexy-components';

export type DossierValidationActionsProps = {
    onCancel?: () => void;
    onSendMessage?: () => void;
    onValidate?: () => void;
    onPublish?: () => void;
    canPublish?: boolean;
    getNotPublishableReason?: string;
};

const DossierValidationActions: React.FunctionComponent<DossierValidationActionsProps> = function (
    props,
) {
    const { hasNonPersistedChanges, processing } = useDossierFormulaireContext();
    const { onCancel, onSendMessage, onValidate, onPublish, canPublish, getNotPublishableReason } =
        props;
    const { palette } = useTheme();
    const [activeButton, setActiveButton] = useState<'save' | 'depose' | null>(null);

    useEffect(() => {
        if (!processing) {
            setActiveButton(null);
        }
    }, [processing]);

    const btnSx = {
        padding: '16px 24px',
        minWidth: 'auto',
        lineHeight: '24px',
    };

    const textBtnSx = {
        ...btnSx,
        color: palette.primary.main,
        '&:hover': {
            color: palette.primary.light,
        },
    };

    const primaryBtnSx = {
        ...btnSx,
        backgroundColor: palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
        '&:hover': {
            backgroundColor: palette.primary.light,
        },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '24px',
                paddingTop: '1em',
                flexWrap: 'wrap',
                position: 'sticky',
                bottom: 0,
                right: 0,
                backgroundColor: palette.background.paper,
                borderRadius: '8px',
            }}
        >
            {onCancel && (
                <Tooltip
                    title={!hasNonPersistedChanges ? "Rien n'a été modifié" : ''}
                    arrow
                    placement="top"
                >
                    <FlexyButton
                        variant="text"
                        sx={textBtnSx}
                        onClick={onCancel}
                        disabled={!hasNonPersistedChanges}
                        tooltip={undoIcon.displayName}
                        aria-label={undoIcon.displayName}
                    >
                        <FaOptionIcon
                            sx={{ lineHeight: 0 }}
                            icon={undoIcon.props.icon}
                        />
                    </FlexyButton>
                </Tooltip>
            )}
            {onSendMessage && (
                <FlexyButton
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={onSendMessage}
                    tooltip={complementaryInfoMailToClientIcon.displayName}
                    aria-label={complementaryInfoMailToClientIcon.displayName}
                >
                    <FaOptionIcon
                        sx={{ lineHeight: 0 }}
                        icon={complementaryInfoMailToClientIcon.props.icon}
                    />
                </FlexyButton>
            )}

            {onValidate && (
                <FlexyButton
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={() => {
                        setActiveButton('save');
                        onValidate();
                    }}
                    disabled={processing}
                    tooltip={saveIcon.displayName}
                    aria-label={saveIcon.displayName}
                >
                    {processing && activeButton === 'save' ? (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    ) : (
                        <FaOptionIcon
                            sx={{ lineHeight: 0 }}
                            icon={saveIcon.props.icon}
                        />
                    )}
                </FlexyButton>
            )}

            {onPublish && (
                <Tooltip
                    title={getNotPublishableReason}
                    arrow
                    placement="top"
                >
                    <FlexyButton
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={() => {
                            setActiveButton('depose');
                            onPublish();
                        }}
                        disabled={processing || canPublish === false}
                        aria-label={depositIcon.displayName}
                        tooltip={depositIcon.displayName}
                    >
                        {processing && activeButton === 'depose' ? (
                            <CircularProgress
                                color="inherit"
                                size="16px"
                            />
                        ) : (
                            <FaOptionIcon
                                sx={{ lineHeight: 0 }}
                                icon={depositIcon.props.icon}
                            />
                        )}
                    </FlexyButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default DossierValidationActions;
